<script setup lang="ts">
import { Sortable } from 'sortablejs-vue3'
import { useStatisticsStore } from './useStatitsticsStore'
import { storeToRefs } from 'pinia'

import {
  IconChartDonut3,
  IconAdjustmentsHorizontal,
  IconSquareFilled,
  IconBoxAlignTopFilled,
  IconBoxAlignBottomFilled,
  IconChartCohort
} from '@tabler/icons-vue'

// Componentes lazy-loaded
const EventInfo = defineAsyncComponent(() => import('./Stats/EventInfo.vue'))
const Personas = defineAsyncComponent(() => import('./Stats/Personas.vue'))
const Tickets = defineAsyncComponent(() => import('./Stats/Tickets.vue'))
const Payments = defineAsyncComponent(() => import('./Stats/Payments.vue'))

const { t } = useI18n()

// Usar el store centralizado
const statisticsStore = useStatisticsStore()
const tabPanePillsActiveKey = ref(1)

// Usar storeToRefs para mantener reactividad
const { showConfig, sortMode, statistics } = storeToRefs(statisticsStore)

// Destructurar las funciones del store
const { loadConfiguration, reorderStatistics, reorderCharts, getChartsConfig } = statisticsStore

const displayOptions = [
  {
    value: 'both',
    label: t('Both'),
    icon: IconSquareFilled
  },
  {
    value: 'chart',
    label: t('Chart'),
    icon: IconBoxAlignTopFilled
  },
  {
    value: 'totals',
    label: t('Totals'),
    icon: IconBoxAlignBottomFilled
  }
]

const components = {
  EventInfo,
  Personas,
  Tickets,
  Payments
}

const onSortChange = (evt: any) => {
  const { oldIndex, newIndex } = evt
  if (oldIndex !== undefined && newIndex !== undefined) {
    reorderStatistics(oldIndex, newIndex)
  }
}

const onChartsReordered = (stat: any, newChartsOrder: any[]) => {
  reorderCharts(stat.component, newChartsOrder)
}

// Cargar configuración al montar el componente
onMounted(() => {
  loadConfiguration()
})

watch(tabPanePillsActiveKey, () => {
  if (tabPanePillsActiveKey.value == 2) sortMode.value = true
  else sortMode.value = false
})
</script>

<template>
  <div class="d-flex align-items-top mb-2 mt-1">
    <IconChartDonut3 class="navbarIcon1" stroke-width="1" size="34" />
    <h3 @click="showConfig = !showConfig" class="cursor-pointer">
      {{ t('Statistics') }}
    </h3>
    <button @click="showConfig = !showConfig" class="btn btn-sm btn-link">
      <IconAdjustmentsHorizontal class="mb-1" />
    </button>
  </div>

  <template v-for="stat in statistics" :key="stat.component">
    <component
      v-if="stat.active"
      :is="components[stat.component]"
      :chartsConfig="stat.hasCharts ? getChartsConfig(stat) : undefined"
      :charts="stat.hasCharts ? stat.charts : undefined"
      :sortMode="sortMode"
      @charts-reordered="onChartsReordered(stat, $event)"
    />
  </template>

  <COffcanvas
    placement="start"
    :backdrop="false"
    scroll
    :visible="showConfig"
    @hide="showConfig = !showConfig"
    style="width: 272px"
  >
    <COffcanvasHeader>
      <COffcanvasTitle>
        <IconChartDonut3 class="navbarIcon1" stroke-width="1.2" size="27" />
        {{ t('Settings') }}
      </COffcanvasTitle>
      <CCloseButton class="text-reset" @click="showConfig = false" />
    </COffcanvasHeader>
    <COffcanvasBody>
      <CNav variant="tabs">
        <CNavItem>
          <CNavLink href="javascript:void(0);" :active="tabPanePillsActiveKey === 1" @click="tabPanePillsActiveKey = 1">
            {{ t('Charts') }}
          </CNavLink>
        </CNavItem>
        <CNavItem>
          <CNavLink href="javascript:void(0);" :active="tabPanePillsActiveKey === 2" @click="tabPanePillsActiveKey = 2">
            {{ t('Sort') }}
          </CNavLink>
        </CNavItem>
      </CNav>
      <div class="mt-4"></div>
      <CTabContent>
        <CTabPane role="tabpanel" aria-labelledby="home-tab" :visible="tabPanePillsActiveKey === 1">
          <div v-for="(stat, i) in statistics" :key="i" class="mb-4">
            <!-- Switch principal del componente -->
            <div class="form-check form-switch">
              <input class="form-check-input" type="checkbox" role="switch" :id="`sw${i}`" v-model="stat.active" />
              <label class="form-check-label fw-bold" :for="`sw${i}`">{{ stat.title }}</label>
            </div>

            <!-- Configuración de gráficos individuales -->
            <div v-if="stat.hasCharts && stat.active && stat.charts" class="mt-2 ms-3">
              <div v-for="(chart, chartIndex) in stat.charts" :key="chart.key" class="mb-3">
                <!-- Switch del gráfico individual -->
                <div class="form-check form-switch">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    role="switch"
                    :id="`charts${i}_${chartIndex}`"
                    v-model="chart.active"
                  />
                  <label class="form-check-label" :for="`charts${i}_${chartIndex}`">{{ chart.title }}</label>
                </div>

                <!-- Opciones de visualización para el gráfico activo -->
                <div v-if="chart.active && chart.displayMode" class="mt-1 d-flex">
                  <div class="btn-group btn-group-sm" role="group">
                    <template v-for="option in displayOptions" :key="option.value">
                      <input
                        type="radio"
                        class="btn-check"
                        :name="`display${i}_${chartIndex}`"
                        :id="`${option.value}${i}_${chartIndex}`"
                        :value="option.value"
                        v-model="chart.displayMode"
                      />
                      <label
                        class="btn btn-secondary btn-sm d-flex flex-column justify-content-center"
                        :for="`${option.value}${i}_${chartIndex}`"
                      >
                        <component :is="option.icon" size="20" stroke-width="1.3" />
                      </label>
                    </template>
                  </div>
                  <input
                    type="checkbox"
                    class="btn-check"
                    name="legend"
                    :id="`legend${chart.key}`"
                    v-model="chart.legend"
                    :value="chart.legend"
                  />

                  <label
                    class="ms-3 btn btn-secondary btn-sm d-flex flex-column justify-content-center"
                    :for="`legend${chart.key}`"
                  >
                    <IconChartCohort size="20" stroke-width="1.3" />
                  </label>
                </div>
              </div>
            </div>
          </div>
        </CTabPane>
        <CTabPane role="tabpanel" aria-labelledby="profile-tab" :visible="tabPanePillsActiveKey === 2">
          <Sortable :list="statistics" item-key="component" tag="ul" class="list-group mt-4" @end="onSortChange">
            <template #item="{ element, index }">
              <li :class="{ 'text-muted': element.active == false }" class="list-group-item isSortList">
                {{ element.title }}
              </li>
            </template>
          </Sortable>
        </CTabPane>
      </CTabContent>
    </COffcanvasBody>
  </COffcanvas>
</template>
<style>
.ghost {
  opacity: 0.4;
}
</style>
